import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { MdSave, MdSend, MdCheckCircle, MdError, MdWarning } from 'react-icons/md';

const SmtpSettings = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  
  // Popup state'i - diğer sayfalardaki popup şablonuna uygun
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success',
    showCancel: false,
    onConfirm: null
  });
  
  // SMTP Ayarları State'i
  const [smtpSettings, setSmtpSettings] = useState({
    enabled: false,
    host: '',
    port: 587,
    secure: false, // true = SSL/TLS, false = STARTTLS
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    reply_to: '',
    cc_mail: '', // CC email adresi
    bcc_mail: '' // BCC email adresi
  });

  // Test Mail Ayarları
  const [testEmail, setTestEmail] = useState({
    to: '',
    subject: t('smtpSettings.testEmailSubject'),
    message: t('smtpSettings.testEmailMessage')
  });

  // Mail Gönderme Kuralları
  const [mailRules, setMailRules] = useState({
    case_created: true,           // Yeni bir case açıldığında
    case_status_changed: true,    // Mevcut case status değiştiğinde
    case_assigned: true,          // Case birine atandığında
    case_severity_changed: true,  // Case severity değiştiğinde
    case_activity: true           // Case içerisine aktivite girildiğinde
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setCompanyId(userData.companyId);
      fetchSmtpSettings(userData.companyId);
    }
  }, []);

  // Popup yönetimi fonksiyonları - diğer sayfalardaki pattern'e uygun
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || t('smtpSettings.notification'),
      message: options.message,
      type: options.type || 'success',
      showCancel: options.showCancel ?? false,
      onConfirm: options.onConfirm || null
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  const confirmPopup = () => {
    if (popup.onConfirm) {
      popup.onConfirm();
    }
    closePopup();
  };

  // SMTP Ayarlarını Getir
  const fetchSmtpSettings = async (companyId) => {
    try {
      setLoading(true);
      const response = await api.get(`/smtp-settings/${companyId}`);
      if (response.data.success) {
        setSmtpSettings(response.data.settings);
        setMailRules(response.data.mailRules);
      }
    } catch (error) {
      console.error('SMTP ayarları yüklenirken hata:', error);
      // İlk kez oluşturuluyorsa varsayılan değerler kullanılır
    } finally {
      setLoading(false);
    }
  };

  // SMTP Ayarlarını Kaydet
  const handleSaveSettings = async () => {
    try {
      setSaveLoading(true);
      const response = await api.post('/smtp-settings', {
        ...smtpSettings,
        mailRules,
        company_id: companyId
      });

      if (response.data.success) {
        showPopup({
          title: t('smtpSettings.success'),
          message: t('smtpSettings.settingsSavedSuccess'),
          type: 'success'
        });
      }
    } catch (error) {
      showPopup({
        title: t('smtpSettings.error'),
        message: error.response?.data?.message || t('smtpSettings.errorSavingSettings'),
        type: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  // SMTP Bağlantı Testi
  const handleTestConnection = async () => {
    try {
      setTestLoading(true);
      const response = await api.post('/smtp-settings/test-connection', {
        ...smtpSettings,
        company_id: companyId
      });

      if (response.data.success) {
        showPopup({
          title: t('smtpSettings.connectionTestSuccess'),
          message: response.data.message || t('smtpSettings.connectionTestSuccessMessage'),
          type: 'success'
        });
      }
    } catch (error) {
      showPopup({
        title: t('smtpSettings.connectionTestFailed'),
        message: error.response?.data?.message || t('smtpSettings.connectionTestFailedMessage'),
        type: 'error'
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Test Maili Gönder
  const handleSendTestEmail = async () => {
    if (!testEmail.to) {
      showPopup({
        title: t('smtpSettings.warning'),
        message: t('smtpSettings.enterTestEmailAddress'),
        type: 'error'
      });
      return;
    }

    try {
      setSendTestLoading(true);
      const response = await api.post('/smtp-settings/send-test', {
        ...smtpSettings,
        testEmail,
        company_id: companyId
      });

      if (response.data.success) {
        showPopup({
          title: t('smtpSettings.testEmailSuccess'),
          message: t('smtpSettings.testEmailSuccessMessage'),
          type: 'success'
        });
      }
    } catch (error) {
      showPopup({
        title: t('smtpSettings.testEmailFailed'),
        message: error.response?.data?.message || t('smtpSettings.testEmailFailedMessage'),
        type: 'error'
      });
    } finally {
      setSendTestLoading(false);
    }
  };

  // Input değişikliklerini handle et
  const handleSmtpChange = (field, value) => {
    setSmtpSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMailRulesChange = (field, value) => {
    setMailRules(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return <div className="p-6">{t('smtpSettings.loading')}</div>;
  }

  return (
    <div className="h-full overflow-auto">
      <div className="w-full">
        {/* Başlık */}
        <div className="mb-6">
          <h1 className="text-2xl font-medium text-gray-800 dark:text-white mb-2">
            {t('smtpSettings.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('smtpSettings.description')}
          </p>
        </div>

        {/* Popup */}
        {popup.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
            <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
              {/* Header */}
              <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-medium">{popup.title}</h3>
              </div>
              
              <div className="p-8 bg-white dark:bg-gray-800">
                <div className="text-center">
                  <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                    {popup.message}
                  </p>
                  <div className="flex justify-center gap-4">
                    {popup.showCancel && (
                      <button
                        onClick={closePopup}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                      >
                        {t('smtpSettings.cancel')}
                      </button>
                    )}
                    <button
                      onClick={confirmPopup}
                      className={`px-4 py-2 text-white rounded-md ${
                        popup.type === 'error' 
                          ? 'bg-red-600 hover:bg-red-700'
                          : popup.type === 'success'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-[#272c39] hover:bg-[#1e2230]'
                      }`}
                    >
                      {t('smtpSettings.ok')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Sol Kolon - SMTP Konfigürasyonu */}
          <div className="xl:col-span-1">
            {/* SMTP Konfigürasyonu */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              {/* Tablo Başlığı */}
              <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 rounded-tl-lg rounded-tr-lg">
                <h2 className="text-lg font-medium text-gray-800 dark:text-white">
                  {t('smtpSettings.smtpConfiguration')}
                </h2>
              </div>
              
              <div className="p-5">
                <div className="space-y-3">
                  {/* E-posta Gönderimi Toggle */}
                  <div className="flex items-center justify-between">
                 
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={smtpSettings.enabled}
                        onChange={(e) => handleSmtpChange('enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Hızlı Ayarlar */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('smtpSettings.quickSettings')}
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          handleSmtpChange('host', 'smtp.office365.com');
                          handleSmtpChange('port', 587);
                          handleSmtpChange('secure', false);
                        }}
                        className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded border hover:bg-blue-200 dark:hover:bg-blue-800"
                      >
                        Office 365
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleSmtpChange('host', 'smtp.gmail.com');
                          handleSmtpChange('port', 587);
                          handleSmtpChange('secure', false);
                        }}
                        className="px-3 py-1 text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded border hover:bg-red-200 dark:hover:bg-red-800"
                      >
                        Gmail
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleSmtpChange('host', 'smtp-mail.outlook.com');
                          handleSmtpChange('port', 587);
                          handleSmtpChange('secure', false);
                        }}
                        className="px-3 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded border hover:bg-purple-200 dark:hover:bg-purple-800"
                      >
                        Outlook
                      </button>
                    </div>
                    
                                       </div>

                   {/* Host */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('smtpSettings.smtpHost')}
                    </label>
                    <input
                      type="text"
                      value={smtpSettings.host}
                      onChange={(e) => handleSmtpChange('host', e.target.value)}
                      placeholder="smtp.office365.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Port ve Güvenlik */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.port')}
                      </label>
                      <input
                        type="number"
                        value={smtpSettings.port}
                        onChange={(e) => handleSmtpChange('port', parseInt(e.target.value))}
                        placeholder="587"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.security')}
                      </label>
                      <select
                        value={smtpSettings.secure ? 'ssl' : 'tls'}
                        onChange={(e) => handleSmtpChange('secure', e.target.value === 'ssl')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="tls">{t('smtpSettings.starttls')}</option>
                        <option value="ssl">{t('smtpSettings.ssl')}</option>
                      </select>
                    </div>
                  </div>

                                     {/* Kullanıcı Adı */}
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       {t('smtpSettings.usernameEmail')}
                     </label>
                     <input
                       type="email"
                       value={smtpSettings.username}
                       onChange={(e) => handleSmtpChange('username', e.target.value)}
                       placeholder="user@example.com"
                       autoComplete="off"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>

                                      {/* Şifre */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.passwordAppPassword')}
                      </label>
                      <input
                        type="password"
                        value={smtpSettings.password}
                        onChange={(e) => handleSmtpChange('password', e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                   {/* Gönderen E-posta */}
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       {t('smtpSettings.fromEmail')}
                     </label>
                     <input
                       type="email"
                       value={smtpSettings.from_email}
                       onChange={(e) => handleSmtpChange('from_email', e.target.value)}
                       placeholder="noreply@company.com"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>

                   {/* Gönderen Adı */}
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       {t('smtpSettings.fromName')}
                     </label>
                     <input
                       type="text"
                       value={smtpSettings.from_name}
                       onChange={(e) => handleSmtpChange('from_name', e.target.value)}
                       placeholder="Destek Ekibi"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>

                                       {/* Yanıt Adresi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.replyToOptional')}
                      </label>
                      <input
                        type="email"
                        value={smtpSettings.reply_to}
                        onChange={(e) => handleSmtpChange('reply_to', e.target.value)}
                        placeholder="support@company.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* CC Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.ccEmailOptional')}
                      </label>
                      <input
                        type="email"
                        value={smtpSettings.cc_mail}
                        onChange={(e) => handleSmtpChange('cc_mail', e.target.value)}
                        placeholder="cc@company.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* BCC Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.bccEmailOptional')}
                      </label>
                      <input
                        type="email"
                        value={smtpSettings.bcc_mail}
                        onChange={(e) => handleSmtpChange('bcc_mail', e.target.value)}
                        placeholder="bcc@company.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Bağlantı Testi Butonu */}
                    <div className="mt-3">
                      <button
                        onClick={handleTestConnection}
                        disabled={testLoading || !smtpSettings.enabled}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        <MdCheckCircle size={16} />
                        {testLoading ? t('smtpSettings.testing') : t('smtpSettings.testConnection')}
                      </button>
                    </div>
                  </div>
               </div>
             </div>
           </div>

          {/* Sağ Kolon - Test, Kurallar ve Kaydet */}
          <div className="space-y-4">
            {/* Test Bölümü */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              {/* Tablo Başlığı */}
              <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 rounded-tl-lg rounded-tr-lg">
                <h2 className="text-lg font-medium text-gray-800 dark:text-white">
                  {t('smtpSettings.testAndValidation')}
                </h2>
              </div>
              
                             <div className="p-5">
                 <div className="space-y-3">
                   {/* Test Maili */}
                   <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('smtpSettings.testEmailAddress')}
                      </label>
                      <input
                        type="email"
                        value={testEmail.to}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, to: e.target.value }))}
                        placeholder="test@example.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <button
                      onClick={handleSendTestEmail}
                      disabled={sendTestLoading || !smtpSettings.enabled || !testEmail.to}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <MdSend size={16} />
                      {sendTestLoading ? t('smtpSettings.sending') : t('smtpSettings.sendTestEmail')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mail Gönderme Kuralları */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              {/* Tablo Başlığı */}
              <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 rounded-tl-lg rounded-tr-lg">
                <h2 className="text-lg font-medium text-gray-800 dark:text-white">
                  {t('smtpSettings.emailSendingRules')}
                </h2>
              </div>
              
              <div className="p-5">
                <div className="space-y-2">
                  {Object.entries(mailRules).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {key === 'case_created' && t('smtpSettings.whenNewCaseCreated')}
                          {key === 'case_status_changed' && t('smtpSettings.whenCaseStatusChanged')}
                          {key === 'case_assigned' && t('smtpSettings.whenCaseAssigned')}
                          {key === 'case_severity_changed' && t('smtpSettings.whenCaseSeverityChanged')}
                          {key === 'case_activity' && t('smtpSettings.whenCaseActivityAdded')}
                        </label>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => handleMailRulesChange(key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Kaydet Butonu */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <button
                onClick={handleSaveSettings}
                disabled={saveLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdSave size={18} />
                {saveLoading ? t('smtpSettings.saving') : t('smtpSettings.saveSettings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettings;
